import React, { useEffect, useRef, useState } from "react";
import ModalShell from "./ModalShell";
import { DirectoryUser, searchDirectory } from "../connectDirectory";
import { homeCourseLabel } from "../roster";

interface Props {
  // Roster player's current name — prefills the search box but stays
  // editable (the roster entry may be an abbreviation / nickname that
  // won't match the user's full profile name).
  initialQuery: string;
  onLink: (user: DirectoryUser) => void;
  onClose: () => void;
}

// Search-and-link modal for the Connect flow. Type a name, pick a matching
// CaddieGames user; each result shows their home course and (when set)
// handicap so two people with the same name are distinguishable
// ("Chris Dopp (Blue Mountain)" vs "Chris Dopp (Georgian Bay Club)").
export default function ConnectModal({ initialQuery, onLink, onClose }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [loading, setLoading] = useState(false);
  // Bumps on every fetch; a stale response whose id != latest is dropped
  // so a slow earlier query can't overwrite a fast later one.
  const reqId = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqId.current;
    const handle = setTimeout(async () => {
      const users = await searchDirectory(q);
      if (id !== reqId.current) return;
      setResults(users);
      setLoading(false);
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const q = query.trim();

  return (
    <ModalShell
      title="Connect player"
      subtitle="Search for this player's CaddieGames profile"
      onClose={onClose}
      footer={
        <button type="button" className="cg-btn cg-btn-ghost" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <div className="cg-connect">
        <input
          className="cg-edit-input cg-connect-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name"
          autoFocus
          aria-label="Search by name"
        />

        {q.length < 2 ? (
          <p className="cg-subpage-note">Type at least 2 letters to search.</p>
        ) : loading ? (
          <p className="cg-subpage-note">Searching…</p>
        ) : results.length === 0 ? (
          <p className="cg-subpage-note">No matching profiles found.</p>
        ) : (
          <ul className="cg-connect-results">
            {results.map((u) => (
              <li key={u.userId}>
                <button
                  type="button"
                  className="cg-connect-result"
                  onClick={() => onLink(u)}
                >
                  <span className="cg-connect-result-main">
                    <span className="cg-connect-result-name">{u.name}</span>
                    <span className="cg-connect-result-course">
                      {homeCourseLabel(u.homeCourse)}
                    </span>
                  </span>
                  {u.handicapIndex != null && (
                    <span className="cg-connect-result-hcp">
                      HI {u.handicapIndex}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}
